import React, { useEffect, useState, useRef } from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { CheckIcon, ArrowRightIcon, ChevronRightIcon, ChevronDownIcon, BuildingIcon, UsersIcon, BarChartIcon, FileTextIcon, ShieldIcon, HeartIcon, BriefcaseIcon, UserIcon, DatabaseIcon, ServerIcon, GlobeIcon, BookOpenIcon, AwardIcon, SmartphoneIcon, RefreshCwIcon, ClipboardCheckIcon, BadgeCheckIcon, ClockIcon, MailIcon, LockIcon, CreditCardIcon, InfinityIcon, LibraryIcon } from 'lucide-react';
export const Homepage = () => {
  return <div className="flex flex-col min-h-screen w-full bg-white text-[#0e1114]" data-id="element-199">
      <style jsx global data-id="element-200">{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        body {
          font-family: 'Plus Jakarta Sans', sans-serif;
        }
        .text-gradient {
          background: linear-gradient(90deg, #0b757a, #1ddbdb);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>
      <Header data-id="element-201" />
      <main className="flex-grow" data-id="element-202">
        <HeroSection data-id="element-203" />
        <TrustStrip data-id="element-204" />
        <WhyKivarroSection data-id="element-205" />
        <UnifiedBenefitsSection data-id="element-206" />
        <ForTeamsSection data-id="element-207" />
        <AllAccessSection data-id="element-208" />
        <EnterpriseAcademySection data-id="element-209" />
        <ExploreTrainingSection data-id="element-210" />
        <ClosingCta data-id="element-211" />
      </main>
      <Footer data-id="element-212" />
    </div>;
};
const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section ref={sectionRef} className="relative pt-32 pb-24 md:pt-40 md:pb-32 lg:pt-44 lg:pb-40 overflow-hidden" data-id="element-213">
      {/* Background image with subtle overlay */}
      <div className="absolute inset-0 z-0" data-id="element-214">
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2074&q=80')",
        opacity: 0.15
      }} data-id="element-215"></div>
      </div>
      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-216">
        <div className="max-w-4xl mx-auto text-center" data-id="element-217">
          <div className={`transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} style={{
          transitionDelay: '100ms'
        }} data-id="element-218">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-tight mb-8 text-[#0e1114]" data-id="element-219">
              Train. Upskill. <br className="hidden sm:block" data-id="element-220" />
              Stay Compliant.
            </h1>
          </div>
          <div className={`transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} style={{
          transitionDelay: '300ms'
        }} data-id="element-221">
            <p className="text-xl md:text-2xl text-[#0e1114]/80 mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-222">
              Accredited compliance, workplace skills, and business training —
              built for Ireland, the UK, and Europe.
            </p>
          </div>
          <div className={`flex flex-col sm:flex-row gap-5 justify-center transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} style={{
          transitionDelay: '500ms'
        }} data-id="element-223">
            <a href="/training" className="px-8 py-4 bg-[#0b757a] text-white font-medium rounded-md transition-all duration-300 hover:bg-[#096469] focus:outline-none focus:ring-2 focus:ring-[#0b757a] focus:ring-offset-2 text-center shadow-lg" data-id="element-224">
              Start Training
            </a>
            <a href="/demo" className="px-8 py-4 border-2 border-[#2b2b2b] text-[#2b2b2b] font-medium rounded-md hover:bg-[#2b2b2b] hover:text-white transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#2b2b2b] focus:ring-offset-2 text-center" data-id="element-225">
              Book a Demo
            </a>
          </div>
        </div>
      </div>
    </section>;
};
const TrustStrip = () => {
  return <section className="py-12 border-y border-[#f4f5f6] bg-white" data-id="element-226">
      <div className="container mx-auto px-4 md:px-6" data-id="element-227">
        <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16" data-id="element-228">
          <div className="flex flex-col items-center" data-id="element-229">
            <span className="text-[#0e1114]/60 text-sm font-medium mb-3" data-id="element-230">
              CPD Accredited
            </span>
            <div className="h-10 w-24 bg-[#f4f5f6] rounded" data-id="element-231"></div>
          </div>
          <div className="flex flex-col items-center" data-id="element-232">
            <span className="text-[#0e1114]/60 text-sm font-medium mb-3" data-id="element-233">
              ROSPA Member
            </span>
            <div className="h-10 w-24 bg-[#f4f5f6] rounded" data-id="element-234"></div>
          </div>
          <div className="flex flex-col items-center" data-id="element-235">
            <span className="text-[#0e1114]/60 text-sm font-medium mb-3" data-id="element-236">
              Irish Trade Federation
            </span>
            <div className="h-10 w-24 bg-[#f4f5f6] rounded" data-id="element-237"></div>
          </div>
          <div className="flex flex-col items-center" data-id="element-238">
            <div className="flex" data-id="element-239">
              {[...Array(5)].map((_, i) => <svg key={i} className="w-5 h-5 text-[#0b757a]" fill="currentColor" viewBox="0 0 20 20" data-id="element-240">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-241" />
                </svg>)}
            </div>
            <span className="mt-2 text-[#0e1114]/60 text-sm font-medium" data-id="element-242">
              97% Completion Rate in 2024
            </span>
          </div>
        </div>
      </div>
    </section>;
};
const WhyKivarroSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section ref={sectionRef} className="py-24 bg-white" data-id="element-243">
      <div className="container mx-auto px-4 md:px-6" data-id="element-244">
        <div className="max-w-3xl mx-auto text-center mb-20" data-id="element-245">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-[#0e1114] tracking-tight" data-id="element-246">
            One academy. Every advantage.
          </h2>
          <p className="text-xl text-[#0e1114]/70 max-w-2xl mx-auto" data-id="element-247">
            Kivarro isn't just courses — it's a training ecosystem. Whether
            you're an individual, a manager, or an enterprise, every learner
            gets the same premium experience.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-248">
          <div className={`bg-[#f4f5f6] rounded-lg p-8 transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} style={{
          transitionDelay: '100ms'
        }} data-id="element-249">
            <div className="w-14 h-14 rounded-full bg-[#0b757a]/10 flex items-center justify-center mb-6" data-id="element-250">
              <UserIcon className="w-7 h-7 text-[#0b757a]" data-id="element-251" />
            </div>
            <h3 className="text-2xl font-bold mb-3 text-[#0e1114]" data-id="element-252">
              For Individuals
            </h3>
            <p className="text-[#0e1114]/70" data-id="element-253">
              Access professional training anytime, anywhere. Get certified
              instantly and boost your career prospects.
            </p>
          </div>
          <div className={`bg-[#0b757a] text-white rounded-lg p-8 transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} style={{
          transitionDelay: '300ms'
        }} data-id="element-254">
            <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center mb-6" data-id="element-255">
              <UsersIcon className="w-7 h-7 text-white" data-id="element-256" />
            </div>
            <h3 className="text-2xl font-bold mb-3" data-id="element-257">For Teams</h3>
            <p className="text-white/90" data-id="element-258">
              Assign, track, and manage training for your entire team with
              built-in dashboards and reporting tools.
            </p>
          </div>
          <div className={`bg-[#2b2b2b] text-white rounded-lg p-8 transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} style={{
          transitionDelay: '500ms'
        }} data-id="element-259">
            <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center mb-6" data-id="element-260">
              <BuildingIcon className="w-7 h-7 text-white" data-id="element-261" />
            </div>
            <h3 className="text-2xl font-bold mb-3" data-id="element-262">For Enterprises</h3>
            <p className="text-white/90" data-id="element-263">
              Scale training across your organization with your own branded
              academy, SSO, and advanced analytics.
            </p>
          </div>
        </div>
      </div>
    </section>;
};
const UnifiedBenefitsSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section ref={sectionRef} className="py-24 bg-[#f4f5f6]" data-id="element-264">
      <div className="container mx-auto px-4 md:px-6" data-id="element-265">
        <div className="max-w-3xl mx-auto text-center mb-20" data-id="element-266">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-[#0e1114] tracking-tight" data-id="element-267">
            Everything you expect. And more.
          </h2>
        </div>
        <div className={`grid grid-cols-12 gap-6 max-w-6xl mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`} data-id="element-268">
          {/* Row 1 */}
          <div className="col-span-12 md:col-span-6 bg-white rounded-lg p-8 shadow-sm flex" data-id="element-269">
            <div className="mr-6" data-id="element-270">
              <div className="w-14 h-14 rounded-full bg-[#0b757a]/10 flex items-center justify-center" data-id="element-271">
                <ClockIcon className="w-7 h-7 text-[#0b757a]" data-id="element-272" />
              </div>
            </div>
            <div data-id="element-273">
              <h3 className="text-2xl font-bold mb-3 text-[#0e1114]" data-id="element-274">
                Start Instantly
              </h3>
              <p className="text-[#0e1114]/70" data-id="element-275">
                Sign up and begin training right away — no delays, no waiting
                periods.
              </p>
            </div>
          </div>
          <div className="col-span-12 md:col-span-6 bg-[#2b2b2b] rounded-lg p-8 text-white flex" data-id="element-276">
            <div className="mr-6" data-id="element-277">
              <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center" data-id="element-278">
                <RefreshCwIcon className="w-7 h-7 text-white" data-id="element-279" />
              </div>
            </div>
            <div data-id="element-280">
              <h3 className="text-2xl font-bold mb-3" data-id="element-281">
                Train Anytime, Anywhere
              </h3>
              <p className="text-white/90" data-id="element-282">
                24/7 access across desktop, mobile, and tablet — learn on your
                schedule.
              </p>
            </div>
          </div>
          {/* Row 2 */}
          <div className="col-span-12 md:col-span-4 bg-white rounded-lg p-8 shadow-sm" data-id="element-283">
            <div className="w-12 h-12 rounded-full bg-[#0b757a]/10 flex items-center justify-center mb-5" data-id="element-284">
              <SmartphoneIcon className="w-6 h-6 text-[#0b757a]" data-id="element-285" />
            </div>
            <h3 className="text-xl font-bold mb-2 text-[#0e1114]" data-id="element-286">
              Dedicated Mobile App
            </h3>
            <p className="text-[#0e1114]/70 text-sm" data-id="element-287">
              Download courses and train offline — even without internet.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 bg-white rounded-lg p-8 shadow-sm" data-id="element-288">
            <div className="w-12 h-12 rounded-full bg-[#0b757a]/10 flex items-center justify-center mb-5" data-id="element-289">
              <BadgeCheckIcon className="w-6 h-6 text-[#0b757a]" data-id="element-290" />
            </div>
            <h3 className="text-xl font-bold mb-2 text-[#0e1114]" data-id="element-291">
              Instant Certification
            </h3>
            <p className="text-[#0e1114]/70 text-sm" data-id="element-292">
              Certificates issued the moment you complete a course.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 bg-[#0b757a] rounded-lg p-8 text-white" data-id="element-293">
            <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mb-5" data-id="element-294">
              <AwardIcon className="w-6 h-6 text-white" data-id="element-295" />
            </div>
            <h3 className="text-xl font-bold mb-2" data-id="element-296">Certificate Guarantee</h3>
            <p className="text-white/90 text-sm" data-id="element-297">
              Always accepted by auditors and regulators.
            </p>
          </div>
          {/* Row 3 */}
          <div className="col-span-12 md:col-span-4 bg-white rounded-lg p-8 shadow-sm" data-id="element-298">
            <div className="w-12 h-12 rounded-full bg-[#0b757a]/10 flex items-center justify-center mb-5" data-id="element-299">
              <LockIcon className="w-6 h-6 text-[#0b757a]" data-id="element-300" />
            </div>
            <h3 className="text-xl font-bold mb-2 text-[#0e1114]" data-id="element-301">
              Stored Forever
            </h3>
            <p className="text-[#0e1114]/70 text-sm" data-id="element-302">
              All certificates and compliance records kept in one secure hub.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 bg-white rounded-lg p-8 shadow-sm" data-id="element-303">
            <div className="w-12 h-12 rounded-full bg-[#0b757a]/10 flex items-center justify-center mb-5" data-id="element-304">
              <InfinityIcon className="w-6 h-6 text-[#0b757a]" data-id="element-305" />
            </div>
            <h3 className="text-xl font-bold mb-2 text-[#0e1114]" data-id="element-306">
              Unlimited Retries
            </h3>
            <p className="text-[#0e1114]/70 text-sm" data-id="element-307">
              Train and retest until you pass — no extra fees.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 bg-[#2b2b2b] rounded-lg p-8 text-white" data-id="element-308">
            <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mb-5" data-id="element-309">
              <LibraryIcon className="w-6 h-6 text-white" data-id="element-310" />
            </div>
            <h3 className="text-xl font-bold mb-2" data-id="element-311">Vast Course Library</h3>
            <p className="text-white/90 text-sm" data-id="element-312">
              Compliance, workplace skills, and business courses that grow with
              you.
            </p>
          </div>
        </div>
      </div>
    </section>;
};
const ForTeamsSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section ref={sectionRef} className="py-24 bg-white" data-id="element-313">
      <div className="container mx-auto px-4 md:px-6" data-id="element-314">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto" data-id="element-315">
          <div className={`transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} data-id="element-316">
            <h2 className="text-4xl md:text-5xl font-bold mb-8 text-[#0e1114] tracking-tight" data-id="element-317">
              Training that scales effortlessly.
            </h2>
            <div className="space-y-6 mb-10" data-id="element-318">
              <div className="flex" data-id="element-319">
                <div className="w-12 h-12 rounded-full bg-[#0b757a]/10 flex items-center justify-center mr-5 flex-shrink-0" data-id="element-320">
                  <MailIcon className="w-6 h-6 text-[#0b757a]" data-id="element-321" />
                </div>
                <div data-id="element-322">
                  <h3 className="text-xl font-bold mb-2 text-[#0e1114]" data-id="element-323">
                    Roll out training instantly
                  </h3>
                  <p className="text-[#0e1114]/70" data-id="element-324">
                    Automated login emails are sent as soon as staff are added.
                  </p>
                </div>
              </div>
              <div className="flex" data-id="element-325">
                <div className="w-12 h-12 rounded-full bg-[#0b757a]/10 flex items-center justify-center mr-5 flex-shrink-0" data-id="element-326">
                  <CreditCardIcon className="w-6 h-6 text-[#0b757a]" data-id="element-327" />
                </div>
                <div data-id="element-328">
                  <h3 className="text-xl font-bold mb-2 text-[#0e1114]" data-id="element-329">
                    Add seats anytime
                  </h3>
                  <p className="text-[#0e1114]/70" data-id="element-330">
                    Scale your training without needing to contact support.
                  </p>
                </div>
              </div>
              <div className="flex" data-id="element-331">
                <div className="w-12 h-12 rounded-full bg-[#0b757a]/10 flex items-center justify-center mr-5 flex-shrink-0" data-id="element-332">
                  <BarChartIcon className="w-6 h-6 text-[#0b757a]" data-id="element-333" />
                </div>
                <div data-id="element-334">
                  <h3 className="text-xl font-bold mb-2 text-[#0e1114]" data-id="element-335">
                    Track progress and export reports
                  </h3>
                  <p className="text-[#0e1114]/70" data-id="element-336">
                    Manage compliance with a built-in dashboard.
                  </p>
                </div>
              </div>
              <div className="flex" data-id="element-337">
                <div className="w-12 h-12 rounded-full bg-[#0b757a]/10 flex items-center justify-center mr-5 flex-shrink-0" data-id="element-338">
                  <RefreshCwIcon className="w-6 h-6 text-[#0b757a]" data-id="element-339" />
                </div>
                <div data-id="element-340">
                  <h3 className="text-xl font-bold mb-2 text-[#0e1114]" data-id="element-341">
                    Reassign licenses
                  </h3>
                  <p className="text-[#0e1114]/70" data-id="element-342">
                    Easily transfer seats as teams change.
                  </p>
                </div>
              </div>
            </div>
            <a href="/teams" className="inline-flex items-center px-8 py-4 bg-[#0b757a] text-white font-medium rounded-md transition-all duration-300 hover:bg-[#096469] focus:outline-none focus:ring-2 focus:ring-[#0b757a] focus:ring-offset-2 shadow-lg" data-id="element-343">
              Train Your Team
              <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-344" />
            </a>
          </div>
          <div className={`transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} style={{
          transitionDelay: '200ms'
        }} data-id="element-345">
            <div className="relative rounded-lg overflow-hidden shadow-2xl" data-id="element-346">
              <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80" alt="Team Training Dashboard" className="w-full h-auto object-cover" data-id="element-347" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0e1114]/50 to-transparent" data-id="element-348"></div>
              <div className="absolute bottom-8 left-8 right-8" data-id="element-349">
                <div className="bg-white p-6 rounded-lg shadow-lg" data-id="element-350">
                  <div className="flex items-center justify-between mb-4" data-id="element-351">
                    <h4 className="text-lg font-bold text-[#0e1114]" data-id="element-352">
                      Team Dashboard
                    </h4>
                    <div className="flex" data-id="element-353">
                      <div className="w-2 h-2 rounded-full bg-[#0b757a] mr-1 animate-pulse" data-id="element-354"></div>
                      <div className="w-2 h-2 rounded-full bg-[#0b757a]/60 mr-1 animate-pulse" style={{
                      animationDelay: '0.2s'
                    }} data-id="element-355"></div>
                      <div className="w-2 h-2 rounded-full bg-[#0b757a]/30 animate-pulse" style={{
                      animationDelay: '0.4s'
                    }} data-id="element-356"></div>
                    </div>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-357">
                    <div data-id="element-358">
                      <div className="text-[#0e1114]/60" data-id="element-359">Assigned</div>
                      <div className="text-xl font-bold text-[#0e1114]" data-id="element-360">24</div>
                    </div>
                    <div data-id="element-361">
                      <div className="text-[#0e1114]/60" data-id="element-362">In Progress</div>
                      <div className="text-xl font-bold text-[#0e1114]" data-id="element-363">18</div>
                    </div>
                    <div data-id="element-364">
                      <div className="text-[#0e1114]/60" data-id="element-365">Completed</div>
                      <div className="text-xl font-bold text-[#0e1114]" data-id="element-366">6</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const AllAccessSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section ref={sectionRef} className="py-24 bg-[#f4f5f6]" data-id="element-367">
      <div className="container mx-auto px-4 md:px-6" data-id="element-368">
        <div className="max-w-6xl mx-auto" data-id="element-369">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-370">
            <div className={`transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} data-id="element-371">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-[#0e1114] tracking-tight" data-id="element-372">
                Unlimited learning. <br data-id="element-373" />
                One subscription.
              </h2>
              <p className="text-xl text-[#0e1114]/70 mb-8" data-id="element-374">
                For learners and businesses who never stop growing, Kivarro
                offers All-Access Membership — one subscription unlocks the
                entire training library.
              </p>
              <div className="space-y-4 mb-10" data-id="element-375">
                <div className="flex items-start" data-id="element-376">
                  <CheckIcon className="w-6 h-6 text-[#0b757a] mt-0.5 mr-3 flex-shrink-0" data-id="element-377" />
                  <p className="text-[#0e1114]/80 text-lg" data-id="element-378">
                    Every course. Every update. Always included.
                  </p>
                </div>
                <div className="flex items-start" data-id="element-379">
                  <CheckIcon className="w-6 h-6 text-[#0b757a] mt-0.5 mr-3 flex-shrink-0" data-id="element-380" />
                  <p className="text-[#0e1114]/80 text-lg" data-id="element-381">
                    Ideal for continuous upskilling and compliance.
                  </p>
                </div>
                <div className="flex items-start" data-id="element-382">
                  <CheckIcon className="w-6 h-6 text-[#0b757a] mt-0.5 mr-3 flex-shrink-0" data-id="element-383" />
                  <p className="text-[#0e1114]/80 text-lg" data-id="element-384">
                    Simple pricing for individuals, teams, and enterprises.
                  </p>
                </div>
              </div>
              <a href="/membership" className="inline-flex items-center px-8 py-4 bg-[#0b757a] text-white font-medium rounded-md transition-all duration-300 hover:bg-[#096469] focus:outline-none focus:ring-2 focus:ring-[#0b757a] focus:ring-offset-2 shadow-lg" data-id="element-385">
                Explore Membership
                <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-386" />
              </a>
            </div>
            <div className={`transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} style={{
            transitionDelay: '200ms'
          }} data-id="element-387">
              <div className="bg-white rounded-xl overflow-hidden shadow-xl" data-id="element-388">
                <div className="bg-[#2b2b2b] p-8 text-white" data-id="element-389">
                  <div className="inline-flex items-center px-3 py-1 bg-[#1ddbdb]/90 text-white text-sm font-medium rounded-full mb-4" data-id="element-390">
                    Most Popular
                  </div>
                  <h3 className="text-3xl font-bold mb-2" data-id="element-391">
                    All-Access Membership
                  </h3>
                  <p className="text-white/80" data-id="element-392">
                    Unlimited training for continuous learning
                  </p>
                </div>
                <div className="p-8" data-id="element-393">
                  <div className="flex items-baseline mb-6" data-id="element-394">
                    <span className="text-4xl font-bold text-[#0e1114]" data-id="element-395">
                      €39
                    </span>
                    <span className="text-[#0e1114]/60 ml-2" data-id="element-396">
                      / month per user
                    </span>
                  </div>
                  <div className="space-y-4 mb-8" data-id="element-397">
                    <div className="flex items-center" data-id="element-398">
                      <CheckIcon className="w-5 h-5 text-[#0b757a] mr-3 flex-shrink-0" data-id="element-399" />
                      <p data-id="element-400">Full access to all 125+ courses</p>
                    </div>
                    <div className="flex items-center" data-id="element-401">
                      <CheckIcon className="w-5 h-5 text-[#0b757a] mr-3 flex-shrink-0" data-id="element-402" />
                      <p data-id="element-403">Unlimited certifications</p>
                    </div>
                    <div className="flex items-center" data-id="element-404">
                      <CheckIcon className="w-5 h-5 text-[#0b757a] mr-3 flex-shrink-0" data-id="element-405" />
                      <p data-id="element-406">New courses as they're released</p>
                    </div>
                    <div className="flex items-center" data-id="element-407">
                      <CheckIcon className="w-5 h-5 text-[#0b757a] mr-3 flex-shrink-0" data-id="element-408" />
                      <p data-id="element-409">Mobile app access</p>
                    </div>
                    <div className="flex items-center" data-id="element-410">
                      <CheckIcon className="w-5 h-5 text-[#0b757a] mr-3 flex-shrink-0" data-id="element-411" />
                      <p data-id="element-412">Team management dashboard</p>
                    </div>
                  </div>
                  <a href="/membership/signup" className="block w-full py-3 bg-[#0b757a] text-white font-medium rounded-md transition-all duration-300 hover:bg-[#096469] text-center" data-id="element-413">
                    Start Free Trial
                  </a>
                  <p className="text-center text-[#0e1114]/60 text-sm mt-3" data-id="element-414">
                    30-day free trial, cancel anytime
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const EnterpriseAcademySection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section ref={sectionRef} className="py-24 bg-[#2b2b2b] text-white" data-id="element-415">
      <div className="container mx-auto px-4 md:px-6" data-id="element-416">
        <div className="max-w-6xl mx-auto" data-id="element-417">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-418">
            <div className={`transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} data-id="element-419">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight" data-id="element-420">
                Your academy. <br data-id="element-421" />
                Your brand.
              </h2>
              <p className="text-white/80 text-xl mb-8" data-id="element-422">
                Go beyond training — launch your own fully branded compliance
                academy.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10" data-id="element-423">
                <div className="flex items-start" data-id="element-424">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-425">
                    <GlobeIcon className="w-5 h-5 text-[#1ddbdb]" data-id="element-426" />
                  </div>
                  <div data-id="element-427">
                    <h3 className="font-bold text-white mb-1" data-id="element-428">
                      Custom branding
                    </h3>
                    <p className="text-white/70 text-sm" data-id="element-429">
                      Your logo, colors, and domain
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-430">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-431">
                    <BarChartIcon className="w-5 h-5 text-[#1ddbdb]" data-id="element-432" />
                  </div>
                  <div data-id="element-433">
                    <h3 className="font-bold text-white mb-1" data-id="element-434">
                      Advanced analytics
                    </h3>
                    <p className="text-white/70 text-sm" data-id="element-435">
                      Detailed reporting and insights
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-436">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-437">
                    <ServerIcon className="w-5 h-5 text-[#1ddbdb]" data-id="element-438" />
                  </div>
                  <div data-id="element-439">
                    <h3 className="font-bold text-white mb-1" data-id="element-440">
                      SSO & provisioning
                    </h3>
                    <p className="text-white/70 text-sm" data-id="element-441">
                      Seamless user management
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-442">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-443">
                    <HeartIcon className="w-5 h-5 text-[#1ddbdb]" data-id="element-444" />
                  </div>
                  <div data-id="element-445">
                    <h3 className="font-bold text-white mb-1" data-id="element-446">
                      Priority support
                    </h3>
                    <p className="text-white/70 text-sm" data-id="element-447">
                      Dedicated account manager
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-448">
                <a href="/enterprise/demo" className="px-8 py-4 bg-white text-[#2b2b2b] font-medium rounded-md transition-all duration-300 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#2b2b2b] text-center shadow-lg" data-id="element-449">
                  Book a Demo
                </a>
                <a href="/enterprise" className="px-8 py-4 border-2 border-white text-white font-medium rounded-md hover:bg-white/10 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#2b2b2b] text-center" data-id="element-450">
                  Learn More
                </a>
              </div>
            </div>
            <div className={`transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} style={{
            transitionDelay: '200ms'
          }} data-id="element-451">
              <div className="relative rounded-lg overflow-hidden shadow-2xl" data-id="element-452">
                <img src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" alt="Enterprise Academy Dashboard" className="w-full h-auto object-cover" data-id="element-453" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2b2b2b]/60 to-transparent" data-id="element-454"></div>
                <div className="absolute top-6 left-6 right-6 flex justify-between items-center" data-id="element-455">
                  <div className="flex items-center bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg" data-id="element-456">
                    <div className="w-6 h-6 bg-[#0b757a] rounded-md mr-3" data-id="element-457"></div>
                    <span className="font-medium text-[#2b2b2b]" data-id="element-458">
                      Your Brand
                    </span>
                  </div>
                  <div className="flex space-x-2" data-id="element-459">
                    <div className="w-3 h-3 bg-white/20 backdrop-blur-sm rounded-full" data-id="element-460"></div>
                    <div className="w-3 h-3 bg-white/20 backdrop-blur-sm rounded-full" data-id="element-461"></div>
                    <div className="w-3 h-3 bg-white/20 backdrop-blur-sm rounded-full" data-id="element-462"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const ExploreTrainingSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const categories = [{
    icon: <ShieldIcon className="w-6 h-6 text-[#0b757a]" data-id="element-463" />,
    title: 'Compliance',
    description: 'Essential regulatory training for all industries.',
    link: '/training/compliance'
  }, {
    icon: <HeartIcon className="w-6 h-6 text-[#0b757a]" data-id="element-464" />,
    title: 'Health & Safety',
    description: 'Workplace safety and risk management training.',
    link: '/training/health-safety'
  }, {
    icon: <BookOpenIcon className="w-6 h-6 text-[#0b757a]" data-id="element-465" />,
    title: 'Food Safety',
    description: 'HACCP and food handling certifications.',
    link: '/training/food-safety'
  }, {
    icon: <UserIcon className="w-6 h-6 text-[#0b757a]" data-id="element-466" />,
    title: 'HR & People',
    description: 'Employee management and workplace culture.',
    link: '/training/hr-people'
  }, {
    icon: <UsersIcon className="w-6 h-6 text-[#0b757a]" data-id="element-467" />,
    title: 'Leadership & Management',
    description: 'Team leadership and management skills.',
    link: '/training/leadership'
  }, {
    icon: <HeartIcon className="w-6 h-6 text-[#0b757a]" data-id="element-468" />,
    title: 'Customer Service',
    description: 'Client relationship and service excellence.',
    link: '/training/customer-service'
  }, {
    icon: <BriefcaseIcon className="w-6 h-6 text-[#0b757a]" data-id="element-469" />,
    title: 'Digital & Marketing',
    description: 'Digital skills and marketing strategies.',
    link: '/training/digital-marketing'
  }, {
    icon: <DatabaseIcon className="w-6 h-6 text-[#0b757a]" data-id="element-470" />,
    title: 'Data, GDPR & Security',
    description: 'Data protection and cybersecurity training.',
    link: '/training/data-security'
  }];
  return <section ref={sectionRef} className="py-24 bg-white" data-id="element-471">
      <div className="container mx-auto px-4 md:px-6" data-id="element-472">
        <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-473">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-[#0e1114] tracking-tight" data-id="element-474">
            Every skill. Every sector.
          </h2>
          <p className="text-xl text-[#0e1114]/70" data-id="element-475">
            From mandatory compliance to leadership, digital skills, and
            business growth.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto" data-id="element-476">
          {categories.map((category, index) => <div key={index} className={`bg-[#f4f5f6] rounded-lg p-6 transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} style={{
          transitionDelay: `${index * 50 + 100}ms`
        }} data-id="element-477">
              <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mb-4" data-id="element-478">
                {category.icon}
              </div>
              <h3 className="text-xl font-bold mb-2 text-[#0e1114]" data-id="element-479">
                {category.title}
              </h3>
              <p className="text-[#0e1114]/70 text-sm mb-4" data-id="element-480">
                {category.description}
              </p>
              <a href={category.link} className="text-[#0b757a] font-medium hover:text-[#1ddbdb] transition-colors flex items-center text-sm" data-id="element-481">
                Explore Courses
                <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-482" />
              </a>
            </div>)}
        </div>
        <div className="text-center mt-12" data-id="element-483">
          <a href="/training" className="px-8 py-4 bg-[#0b757a] text-white font-medium rounded-md transition-all duration-300 hover:bg-[#096469] focus:outline-none focus:ring-2 focus:ring-[#0b757a] focus:ring-offset-2 inline-block shadow-lg" data-id="element-484">
            View All Training Courses
          </a>
        </div>
      </div>
    </section>;
};
const ClosingCta = () => {
  return <section className="py-24 bg-[#f4f5f6]" data-id="element-485">
      <div className="container mx-auto px-4 md:px-6" data-id="element-486">
        <div className="max-w-4xl mx-auto text-center" data-id="element-487">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-[#0e1114] tracking-tight" data-id="element-488">
            The Training Academy for Ireland, the UK & Europe.
          </h2>
          <p className="text-xl text-[#0e1114]/70 mb-10 max-w-3xl mx-auto" data-id="element-489">
            Individuals. Teams. Enterprises. One platform. Instant access.
          </p>
          <div className="flex flex-col sm:flex-row gap-5 justify-center" data-id="element-490">
            <a href="/training" className="px-8 py-4 bg-[#0b757a] text-white font-medium rounded-md transition-all duration-300 hover:bg-[#096469] focus:outline-none focus:ring-2 focus:ring-[#0b757a] focus:ring-offset-2 text-center shadow-lg" data-id="element-491">
              Start Training
            </a>
            <a href="/demo" className="px-8 py-4 border-2 border-[#2b2b2b] text-[#2b2b2b] font-medium rounded-md hover:bg-[#2b2b2b] hover:text-white transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#2b2b2b] focus:ring-offset-2 text-center" data-id="element-492">
              Book a Demo
            </a>
          </div>
        </div>
      </div>
    </section>;
};